import { useEffect, useState } from "react";
import api, { inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { Download } from "lucide-react";

const COLORS = ["#D95D39", "#6A7F66", "#2A9D8F", "#E9C46A", "#F4A261"];

export default function Reports() {
  const today = new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [data, setData] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/reports/sales`, { params: { start_date: start, end_date: end } });
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const summary = data?.summary;
  const modeData = summary ? Object.entries(summary.by_payment_mode).map(([k, v]) => ({ name: k, value: v })) : [];
  const typeData = summary ? Object.entries(summary.by_order_type).map(([k, v]) => ({ name: k, value: v })) : [];

  const exportCSV = () => {
    if (!data?.orders?.length) return;
    const rows = [["Order No", "Date", "Type", "Subtotal", "CGST", "SGST", "Total", "Payment"]];
    data.orders.forEach((o) => {
      rows.push([
        o.order_no, o.paid_at || o.created_at, o.type, o.subtotal, o.cgst, o.sgst, o.total,
        (o.payments || []).map((p) => `${p.mode}:${p.amount}`).join("|"),
      ]);
    });
    const csv = rows.map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `sales-${start}-to-${end}.csv`; a.click();
  };

  return (
    <div className="p-6 sm:p-8" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A]">Reports</h1>
          <p className="text-sm text-[#7A7873] mt-1">Sales, GST, payment breakdown and top items.</p>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 border-[#EBE8E2]" data-testid="report-start" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 border-[#EBE8E2]" data-testid="report-end" /></div>
          <Button onClick={load} className="bg-[#D95D39] hover:bg-[#C24F2C] h-9" data-testid="run-report">Run</Button>
          <Button onClick={exportCSV} variant="outline" className="h-9" data-testid="export-csv"><Download size={14} className="mr-1.5" />CSV</Button>
        </div>
      </div>

      {summary && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <Card className="p-4 border-[#EBE8E2] bg-white">
              <div className="text-xs uppercase tracking-wider text-[#7A7873]">Total Orders</div>
              <div className="font-heading font-extrabold text-2xl text-[#2D2C2A] mt-1 font-mono-num">{summary.total_orders}</div>
            </Card>
            <Card className="p-4 border-[#EBE8E2] bg-white">
              <div className="text-xs uppercase tracking-wider text-[#7A7873]">Total Revenue</div>
              <div className="font-heading font-extrabold text-2xl text-[#D95D39] mt-1 font-mono-num">{inr(summary.total_revenue)}</div>
            </Card>
            <Card className="p-4 border-[#EBE8E2] bg-white">
              <div className="text-xs uppercase tracking-wider text-[#7A7873]">CGST</div>
              <div className="font-heading font-extrabold text-2xl text-[#2D2C2A] mt-1 font-mono-num">{inr(summary.total_cgst)}</div>
            </Card>
            <Card className="p-4 border-[#EBE8E2] bg-white">
              <div className="text-xs uppercase tracking-wider text-[#7A7873]">SGST</div>
              <div className="font-heading font-extrabold text-2xl text-[#2D2C2A] mt-1 font-mono-num">{inr(summary.total_sgst)}</div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <Card className="p-5 border-[#EBE8E2] bg-white">
              <h3 className="font-heading font-bold text-lg mb-3">By Payment Mode</h3>
              <div style={{ width: "100%", height: 250 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={modeData} dataKey="value" nameKey="name" outerRadius={90} label={(d) => d.name}>
                      {modeData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => inr(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card className="p-5 border-[#EBE8E2] bg-white">
              <h3 className="font-heading font-bold text-lg mb-3">By Order Type</h3>
              <div style={{ width: "100%", height: 250 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={typeData} dataKey="value" nameKey="name" outerRadius={90} label={(d) => d.name}>
                      {typeData.map((_, i) => <Cell key={i} fill={COLORS[(i + 1) % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => inr(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <Card className="p-5 border-[#EBE8E2] bg-white mb-6">
            <h3 className="font-heading font-bold text-lg mb-3">Top Items by Revenue</h3>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={summary.top_items} layout="vertical" margin={{ left: 16, right: 16 }}>
                  <CartesianGrid stroke="#EBE8E2" strokeDasharray="2 4" horizontal={false} />
                  <XAxis type="number" tick={{ fill: "#7A7873", fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fill: "#2D2C2A", fontSize: 12 }} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Bar dataKey="revenue" fill="#D95D39" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
